import { ethers } from "ethers";
import erc20Abi from "./abi/ERC20.json";
import atomicQueueAbi from "./abi/AtomicQueue.json";
import accountantAbi from "./abi/AccountantWithRateProviders.json";
import multiCall3Abi from "./abi/MultiCall3.json";
import priceFeedAbi from "./abi/PriceFeed.json";
import BigNumber from "bignumber.js";
import { DateTime } from "luxon";

const _network = ethers.Network.from(1);
const provider = new ethers.JsonRpcProvider("https://mainnet.infura.io/v3/b6bf7d3508c941499b10025c0776eaf8", _network, { staticNetwork: _network, batchMaxCount: 1 });

export const ebtcContract = new ethers.Contract("0x657e8C867D8B37dCC18fA4Caead9C45EB088C642", erc20Abi, provider);
export const wbtcContract = new ethers.Contract("0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599", erc20Abi, provider);
export const lbtcContract = new ethers.Contract("0x8236a87084f8B84306f72007F36F2618A5634494", erc20Abi, provider);
export const cbbtcContract = new ethers.Contract("0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf", erc20Abi, provider);
const lbtcOracleContract = new ethers.Contract("0xb415eAA355D8440ac7eCB602D3fb67ccC1f0bc81", priceFeedAbi, provider);
export const atomicQueueContract = new ethers.Contract("0xD45884B592E316eB816199615A95C182F75dea07", atomicQueueAbi, provider);
const accountantContract = new ethers.Contract("0x1b293DC39F94157fA0D1D36d7e0090C8B8B8c13F", accountantAbi, provider);
const multiCall3Contract = new ethers.Contract("0xcA11bde05977b3631167028862bE2a173976CA11", multiCall3Abi, provider);

const quoteTokens = [
  { symbol: "WBTC", contract: wbtcContract, decimals: 8 },
  { symbol: "LBTC", contract: lbtcContract, decimals: 8 },
  { symbol: "cbBTC", contract: cbbtcContract, decimals: 8 },
];
const allTokens = [
  { symbol: "eBTC", contract: ebtcContract, decimals: 8 },
  ...quoteTokens,
];

export async function getMetadata(addresses: string[]) {
  const ret: any = {
    addresses: addresses.map(address => ({ address, balance: {}, atomicRequest: {} })),
    rates: {},
    solveMetadata: {},
  };

  const calls = [];
  const applyResultFunctions = [];

  calls.push({
    target: lbtcOracleContract.target,
    callData: lbtcOracleContract.interface.encodeFunctionData("latestAnswer"),
  });
  for (const { contract } of quoteTokens) {
    calls.push({
      target: accountantContract.target,
      callData: accountantContract.interface.encodeFunctionData("getRateInQuote", [contract.target]),
    }, {
      target: atomicQueueContract.target,
      callData: atomicQueueContract.interface.encodeFunctionData("viewSolveMetaData", [ebtcContract.target, contract.target, addresses])
    });
  }

  applyResultFunctions.push(data => {
    ret.lbtcRate = lbtcOracleContract.interface.decodeFunctionResult("latestAnswer", data)[0];
    ret.displayLbtcRate = BigNumber(ret.lbtcRate.toString()).shiftedBy(-8).toFixed();
  });
  for (const { symbol, contract, decimals } of quoteTokens) {
    applyResultFunctions.push(data => {
      const rate = accountantContract.interface.decodeFunctionResult("getRateInQuote", data)[0];
      ret.rates[symbol] = {
        value: rate,
        displayValue: BigNumber(rate.toString()).shiftedBy(-8).toFixed(),
      };
    }, data => {
      const solveMetadata = atomicQueueContract.interface.decodeFunctionResult("viewSolveMetaData", data)[0];
      ret.solveMetadata[symbol] = solveMetadata;

      for (let i = 0; i < addresses.length; i++) {
        const addressSolveMetadata = solveMetadata[i];
        const offerAmount = addressSolveMetadata[2];
        if (offerAmount > 0n) {
          ret.addresses[i].atomicRequest[symbol] = {
            status: Number(addressSolveMetadata[1]),
            offerAmount,
            displayOfferAmount: BigNumber(offerAmount.toString()).shiftedBy(-8).toFixed(),
            wantAmount: addressSolveMetadata[3],
            displayWantAmount: BigNumber(addressSolveMetadata[3].toString()).shiftedBy(-decimals).toFixed(),
          };
        }
      }
    });
  }

  for (const addressObj of ret.addresses) {
    const { address } = addressObj;

    calls.push({
      target: multiCall3Contract.target,
      callData: multiCall3Contract.interface.encodeFunctionData("getEthBalance", [address]),
    }, {
      target: ebtcContract.target,
      callData: ebtcContract.interface.encodeFunctionData("allowance", [address, atomicQueueContract.target])
    });
    for (const { contract } of allTokens) {
      calls.push({
        target: contract.target,
        callData: contract.interface.encodeFunctionData("balanceOf", [address])
      });
    }
    for (const { contract } of quoteTokens) {
      calls.push({
        target: atomicQueueContract.target,
        callData: atomicQueueContract.interface.encodeFunctionData("getUserAtomicRequest", [address, ebtcContract.target, contract.target])
      });
    }

    applyResultFunctions.push(data => {
      addressObj.ethBalance = multiCall3Contract.interface.decodeFunctionResult("getEthBalance", data)[0];
      addressObj.displayEthBalance = BigNumber(addressObj.ethBalance.toString()).shiftedBy(-18).toFixed();
    }, data => {
      addressObj.ebtcAllowance = ebtcContract.interface.decodeFunctionResult("allowance", data)[0];
    });
    for (const { contract, symbol, decimals } of allTokens) {
      applyResultFunctions.push(data => {
        const value = contract.interface.decodeFunctionResult("balanceOf", data)[0];
        addressObj.balance[symbol] = {
          value,
          displayValue: BigNumber(value).shiftedBy(-decimals).toFixed(),
        };
      });
    }
    for (const { symbol } of quoteTokens) {
      applyResultFunctions.push(data => {
        if (addressObj.atomicRequest[symbol]) {
          const [unixDeadline, price] = atomicQueueContract.interface.decodeFunctionResult("getUserAtomicRequest", data)[0];
          const deadline = DateTime.fromSeconds(Number(unixDeadline));
          if (deadline.diffNow().toMillis() > 0) {
            Object.assign(addressObj.atomicRequest[symbol], {
              deadline,
              displayDeadline: deadline.toFormat("yyyy-MM-dd HH:mm"),
              displayTimeLeft: deadline.diffNow().toFormat("d'd' hh:mm:ss"),
              price,
              displayPrice: BigNumber(price.toString()).shiftedBy(-8).toFixed(),
            });
          } else {
            addressObj.atomicRequest[symbol] = undefined;
          }
        }
      });
    }
  }

  const multiCallRet = await multiCall3Contract.tryAggregate(false, calls);
  for (let i = 0; i < calls.length; i++) {
    if (multiCallRet[i][0]) {
      applyResultFunctions[i](multiCallRet[i][1]);
    }
  }

  return ret;
}
